// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CreateNewGroupModal from "@bg/App/Components/MyCustomQuotes/CreateGroupModal";
import QuoteTotal from "@bg/App/Components/MyCustomQuotes/CustomQuoteTotalSummary";
import GroupActions from "@bg/App/Components/MyCustomQuotes/GroupActions";
import GroupItemsHeader from "@bg/App/Components/MyCustomQuotes/LinesHeader";
import QuoteLine from "@bg/App/Components/MyCustomQuotes/MyCustomQuotesQuoteLine";
import Notify from "@bg/App/Components/Notify";
import LinesHeader from "@bg/App/Components/RfqQuoteDetails/RelatedLinesHeader";
import RelatedProductItem from "@bg/App/Components/RfqQuoteDetails/RelatedProductItem";
import RelatedProductSelector from "@bg/App/Components/RfqQuoteDetails/RelatedProductSearchField";
import SaveQtySticky from "@bg/App/Components/SaveQty/SaveQtySticky";
import addToQuote from "@bg/App/Handlers/CustomQuote/AddNewCustomQuoteLine";
import moveLines from "@bg/App/Handlers/CustomQuote/MoveCustomQuoteLines";
import renameGroup from "@bg/App/Handlers/CustomQuote/RenameGroup";
import setSelectedLines from "@bg/App/Handlers/CustomQuote/SetSelectedLines";
import updateLineQty from "@bg/App/Handlers/CustomQuote/UpdateLineQty";
import updateLineOrder from "@bg/App/Handlers/CustomQuote/UpdateLineSortOrder";
import { CustomQuoteModel, GroupLineModel } from "@bg/App/Services/CustomQuoteService";
import { accodionStyle } from "@bg/App/SharedStyles/GroupsGeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { getTemporaryEmptyLists } from "@bg/App/Store/Selectors/MyCustomProductListDetails/MyCustomProductListDetailsSelector";
import MyCustomQuoteDetailsOptions from "@bg/App/Widgets/MyCustomQuoteDetails/MyCustomQuoeDetailsOptions";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { updateGroupNameHandler } from "@bg/XDL/Utilities/groupName";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import Accordion from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { ReactSortable, Sortable, SortableEvent, Store } from "react-sortablejs";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    quoteState: state.myCustomQuoteDetails,
    uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
    addOnsGroupName: (getSettingsCollection(state).customSettings as any).addOnsGroupName as string,
    isRelatedItemLoading: state.data.products.isLoading,
    relatedProductQty: state.commonActions["relatedProductQty"],
    temporaryEmptyLists: getTemporaryEmptyLists(state),
    productLinesQtyToUpdate: state.myCustomQuoteDetails.productLinesQtyToUpdate,
    groupsNamingRegex: (getSettingsCollection(state).customSettings as any).groupsNamingRegex as string,
});

const mapDispatchToProps = {
    renameGroup,
    moveLines,
    updateLineOrder,
    setSelectedLines,
    addToQuote,
    updateLineQty,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface MyCustomQuoteProductListStyles {
    centeringWrapper?: InjectableCss;
    quoteTotal?: InjectableCss;
    totalLineWrapper?: InjectableCss;
    quoteTotalValue?: TypographyPresentationProps;
    quoteTotalText?: TypographyPresentationProps;
    searchAndProductSection?: InjectableCss;
    addRelatedProductCopy?: TypographyPresentationProps;
    relatedProductWrapper?: InjectableCss;
    saveQtyButtonWrapper?: InjectableCss;
    saveQtyButton?: ButtonPresentationProps;
    notifyStyle?: InjectableCss;
}

export const myCustomQuoteProductListStyles: MyCustomQuoteProductListStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            width: 100%;
            align-items: center;
            justify-content: center;
        `,
    },
    quoteTotal: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            margin-bottom: 20px;
        `,
    },
    totalLineWrapper: {
        css: css`
            display: flex;
        `,
    },
    quoteTotalValue: {
        weight: fontConfig.weight.semiBold,
        css: css`
            min-width: 90px;
            text-align: right;
        `,
    },
    quoteTotalText: {
        weight: fontConfig.weight.semiBold,
        css: css`
            min-width: 80px;
        `,
    },
    searchAndProductSection: {
        css: css`
            margin-bottom: 40px;
        `,
    },
    addRelatedProductCopy: {
        lineHeight: fontConfig.lineHeight.small,
        css: css`
            display: block;
            margin-bottom: ${fontConfig.spacing.heading};
        `,
    },
    relatedProductWrapper: {
        css: css`
            min-height: 200px;
        `,
    },
    notifyStyle: {
        css: css`
            padding-top: 15px;
        `,
    },
};

type GroupLineModelWithId = GroupLineModel & { id: string };

const styles = { ...myCustomQuoteProductListStyles };

const MyCustomQuoteDetailsProductList = ({
    quoteState,
    id,
    uncategorizedGroupName,
    addOnsGroupName,
    renameGroup,
    moveLines,
    updateLineOrder,
    setSelectedLines,
    isRelatedItemLoading,
    relatedProductQty,
    addToQuote,
    toaster,
    temporaryEmptyLists,
    productLinesQtyToUpdate,
    updateLineQty,
    groupsNamingRegex,
}: Props) => {
    const quote: CustomQuoteModel = quoteState.quote;

    const [listGroups, setListGroups] = useState<string[]>([]);
    const [changeGroupNameModalIsOpen, setChangeGroupNameModalIsOpen] = useState(false);
    const [createNewModalIsOpen, setCreateNewModalIsOpen] = useState(false);
    const [newCreatedGroupName, setNewGroupName] = useState("");
    const [groupNameError, setGroupNameError] = useState("");
    const [addRelatedProduct, setAddRelatedProduct] = useState(false);
    const [relatedError, setRelatedError] = useState("");
    const [relatedQuoteLine, setRelatedQuoteLine] = useState<GroupLineModel | undefined>(undefined);
    const [relatedProductGroup, setRelatedProductGroup] = useState("");
    const [relatedProducts, setRelatedProducts] = useState<ProductModel[]>([]);
    const [relatedProductInfos, setRelatedProductInfos] = useState<ProductInfo[]>([]);

    const [groups, setGroups] = useState<{ [key: string]: GroupLineModelWithId[] }>({});
    const newGroupName = useRef("");
    const oldGroupName = useRef("");
    const draggedLine = useRef(0);

    const [loaded, setIsLoaded] = useState(false);

    const dispatch = useAppDispatch();

    React.useEffect(() => {
        if (!loaded && quote?.groupLinesModel) {
            setIsLoaded(true);
        }
    }, [quote?.groupLinesModel]);

    useEffect(() => {
        if (quote && quote.groups) {
            const listGroups = uniq(quote.groups.split(","));
            setListGroups(listGroups);
        }
    }, [quote, quoteState]);

    useEffect(() => {
        if (quote && quote.groupLinesModel && !quoteState.isLoading) {
            // Should be done this way to allow object extensibility
            const quoteGroupLines = {} as { [key: string]: GroupLineModelWithId[] };
            Object.keys(quote.groupLinesModel).forEach(group => {
                quoteGroupLines[group] = [
                    ...quote.groupLinesModel[group].map(groupLine => {
                        return {
                            ...groupLine,
                            id: groupLine.lineId,
                        };
                    }),
                ];
            });
            setGroups(quoteGroupLines);
        }
    }, [quote, quote?.groupLinesModel, quoteState?.isLoading]);

    // set items' group name on drag start
    const onStart = useCallback((e: SortableEvent, sortable: Sortable | null, store: Store, originalGroup: string) => {
        draggedLine.current = e.oldIndex!;
        oldGroupName.current = originalGroup;
    }, []);

    // sort update inside the group only
    const onUpdate = useCallback(
        (e: SortableEvent, group: string) => {
            const quoteLine = quote?.groupLinesModel[group][e.oldIndex!];
            const linesModel = [
                {
                    lineId: quoteLine.lineId,
                    sortOrder: e.newIndex! + 1,
                },
            ];

            updateLineOrder({
                customQuoteId: quote.id,
                updateSortOrder: true,
                linesModel,
            });
        },
        [quote?.groupLinesModel, quote?.id, updateLineOrder],
    );

    // adding item to a new group - using async to make sure state is updated and avoid console warnings
    const onAdd = useCallback(
        (e: SortableEvent, newGroup: string) => {
            const draggedItem = quote?.groupLinesModel[oldGroupName.current][draggedLine.current];
            const linesModel: any = [
                {
                    lineId: draggedItem?.lineId,
                    sortOrder: e.newIndex! + 1,
                },
            ];

            const ended = onEnd();
            if (ended) {
                moveLines({
                    customQuoteId: quote.id,
                    newGroupName: newGroup,
                    linesModel,
                    onComplete: (result: any) => {
                        if (result.successful) {
                            setSelectedLines({
                                lineIds: [draggedItem.lineId],
                                groupName: oldGroupName.current,
                                clearAll: true,
                            });
                        }
                    },
                });
            }
        },
        [moveLines, quote?.groupLinesModel, quote?.id, setSelectedLines],
    );

    const mutateNewGroup = (newState: GroupLineModelWithId[], group: string) => {
        // Returns the new state for all groups
        // for all the groups, newState is the state of the group:group
        const filteredNewState = uniqBy(newState, "id");
        setGroups(oldGroups => {
            const newGroupsState = {
                ...oldGroups,
                [group]: filteredNewState.map((newItemState, index) => ({ ...newItemState, sortOrder: index })),
            };

            return newGroupsState;
        });
    };

    const syncDataOnEnd = (event: SortableEvent) => {
        // Moving item inside the same group
        if (oldGroupName.current === newGroupName.current && event.oldIndex !== event.newIndex) {
            onUpdate(event, newGroupName.current);
        }
        if (oldGroupName.current !== newGroupName.current) {
            onAdd(event, newGroupName.current);
        }
        newGroupName.current = "";
        oldGroupName.current = "";
    };

    // on drag end
    const onEnd = () => {
        oldGroupName.current = "";
        setNewGroupName("");
        return true;
    };

    const openGroupNameModal = (key: string) => {
        setNewGroupName(key);
        oldGroupName.current = key;
        setChangeGroupNameModalIsOpen(true);
    };

    const handleChangeGroupModalClose = () => {
        setNewGroupName("");
        setGroupNameError("");
        setChangeGroupNameModalIsOpen(false);
    };

    const handleNewGroupNameChange = (e: any) => {
        setNewGroupName(e.target.value);
    };

    const updateGroupName = () => {
        const { newGroupName, error } = updateGroupNameHandler(newCreatedGroupName, groupsNamingRegex, listGroups);
        if (error) {
            setGroupNameError(error);
            return;
        }

        renameGroup({
            customQuoteId: quote.id,
            originalGroupName: oldGroupName.current.trim(),
            newGroupName,
        });
        setChangeGroupNameModalIsOpen(false);
    };

    const getGrandTotal = (key: string) => {
        let groupTotal = 0;
        if (quote && quote?.groupLinesModel && quote?.groupLinesModel[key]?.length > 0) {
            quote.groupLinesModel[key].forEach(line => {
                groupTotal += line.totalQuotePrice;
                if (line.relatedItems && line.relatedItems.length > 0) {
                    line.relatedItems.forEach(related => {
                        groupTotal += related.totalQuotePrice;
                    });
                }
            });
        }
        return groupTotal;
    };

    const openCreateNewGroup = () => {
        setCreateNewModalIsOpen(true);
    };

    const handleCloseCreateNewGroup = () => {
        setCreateNewModalIsOpen(false);
    };

    const callAfterSuccess = (selectedGroup: string) => {
        dispatch({
            type: "Pages/xProductListDetails/TemporarilySaveEmptyList",
            emptyList: selectedGroup,
        });
    };

    const addRelatedProductHandler = (quoteLine: GroupLineModel, group: string) => {
        resetAddRelatedModal();
        setRelatedQuoteLine(quoteLine);
        setRelatedProductGroup(group);
        setAddRelatedProduct(true);
    };

    const onSelectRelatedProduct = (productInfo: ProductInfo, product: ProductModel) => {
        if (!relatedProductGroup || !relatedQuoteLine) {
            return;
        }

        const isAlreadyAddedAsRelated = relatedQuoteLine.relatedItems?.filter(
            line => line.productResult.id === product.id,
        )?.length;

        if (isAlreadyAddedAsRelated || relatedProducts?.filter(i => i.id === product.id).length > 0) {
            setRelatedError("Item is already added as a related product");
            return;
        }

        let isLareadyAddedAsQuoteLine = false;
        Object.keys(quote?.groupLinesModel).forEach(group => {
            quote?.groupLinesModel[group].forEach(line => {
                if (line.productResult.id === product.id) {
                    isLareadyAddedAsQuoteLine = true;
                }
            });
        });
        if (isLareadyAddedAsQuoteLine) {
            setRelatedError("Item is already added to quote");
            return;
        }

        const l1 = relatedQuoteLine.relatedItems ? relatedQuoteLine.relatedItems.length : 0;
        const l2 = relatedProducts.length;

        // plus 1 which is the current selected product, 5 since we are yet haven't added the product to the related ones
        if (product && l1 + l2 + 1 > 6) {
            setRelatedError("Max allowed related products is 6");
            return;
        }

        setRelatedError("");
        setRelatedProducts([...relatedProducts, product]);
        setRelatedProductInfos([...relatedProductInfos, productInfo]);
    };

    const resetAddRelatedModal = () => {
        setRelatedProducts([]);
        setRelatedProductInfos([]);
        setRelatedQuoteLine(undefined);
        setRelatedError("");
        setRelatedProductGroup("");
        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "relatedProductQty",
                value: {},
            },
        });
    };

    const closeRelatedProductModalHandler = () => {
        resetAddRelatedModal();
        setAddRelatedProduct(false);
    };

    const addRelatedProductToQuote = async () => {
        setRelatedError("");
        if (!relatedProducts.length || !relatedProductInfos.length) {
            return;
        }
        const productInfos: ProductInfo[] = [];
        const linesModel = [] as {
            qty: number;
            productErpNumber: string;
            isRelatedProduct: boolean;
            parentId: string;
        }[];

        relatedProducts.forEach((relatedProduct, index) => {
            productInfos.push({
                productId: relatedProduct.id,
                qtyOrdered: relatedProductQty?.[relatedProduct.id] || relatedProductInfos[index].qtyOrdered,
                unitOfMeasure: relatedProductInfos[index].unitOfMeasure ?? "",
            } as ProductInfo);
            linesModel.push({
                productErpNumber: relatedProduct.productNumber,
                qty: relatedProductQty?.[relatedProduct.id] || relatedProductInfos[index].qtyOrdered,
                isRelatedProduct: true,
                parentId: relatedQuoteLine!.lineId,
            });
        });
        await addToQuote({
            customQuoteId: quote.id,
            groupName: relatedProductGroup,
            linesModel,
            onComplete(res: any) {
                if (res.apiParameter?.linesModel?.length) {
                    closeRelatedProductModalHandler();
                    toaster.addToast({ body: translate("Items Added"), messageType: "success" });
                }
            },
        });
    };

    const isLoadingRelated =
        Object.keys(isRelatedItemLoading)?.filter(x => isRelatedItemLoading[x] === true).length > 0;

    const listGroupsNames = listGroups.map(listName => listName.toLocaleLowerCase());

    const updateProductsQty = () => {
        if (productLinesQtyToUpdate.length === 0) {
            return;
        }

        updateLineQty({
            customQuoteId: quote.id,
            linesModel: productLinesQtyToUpdate,
            updateQty: true,
            onComplete: _ => {
                dispatch({
                    type: "Pages/MyCustomQuotes/ClearProductsQty",
                });
            },
        });
    };

    if (!quoteState.quote) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <LoadingOverlay loading={quoteState.isLoading && !loaded}>
            <MyCustomQuoteDetailsOptions quote={quote} widgetId={id} />
            <StyledWrapper data-test-selector="linesContainer">
                {listGroupsNames?.map(key => {
                    // Xng-Change BG-2421: hide empty groups unless they were recently created.
                    if (
                        quote.groupLinesModel[key] ||
                        key.toLowerCase() === addOnsGroupName.toLowerCase() ||
                        temporaryEmptyLists.some(emptyList => emptyList.toLowerCase() === key.toLowerCase())
                    ) {
                        // Xng-Change end.
                        return (
                            <React.Fragment key={`${key}`}>
                                {key.toLowerCase() === addOnsGroupName.toLowerCase() && (
                                    <CreateNewGroupModal
                                        onClick={openCreateNewGroup}
                                        closeModalCallback={handleCloseCreateNewGroup}
                                        callAfterSuccess={callAfterSuccess}
                                        groupingModalIsOpen={createNewModalIsOpen}
                                        showButton={true}
                                        variant="button"
                                        quote={quote}
                                        creatingNewGroup={true}
                                    />
                                )}
                                <StyledWrapper {...accodionStyle.groupWrapper} className="no-borders">
                                    <Accordion headingLevel={1}>
                                        <ManagedAccordionSection
                                            {...accodionStyle.manageAccodiornSectionStyles}
                                            title={
                                                <StyledWrapper {...accodionStyle.accrdionTitle}>
                                                    <Typography color="common.white">
                                                        {key.toUpperCase()}&nbsp;&nbsp;
                                                        {key.toLowerCase() !== uncategorizedGroupName.toLowerCase() &&
                                                            key.toLowerCase() !== addOnsGroupName.toLowerCase() && (
                                                                <Icon
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        openGroupNameModal(key);
                                                                    }}
                                                                    src="Create"
                                                                    size={13}
                                                                    color="common.white"
                                                                />
                                                            )}
                                                    </Typography>
                                                    <Typography
                                                        transform="uppercase"
                                                        color="common.white"
                                                        css={css`
                                                            margin-right: 5px;
                                                        `}
                                                    >
                                                        {translate("Grand Total:")}&nbsp;&nbsp;
                                                        <LocalizedCurrency amount={getGrandTotal(key)} />
                                                    </Typography>
                                                </StyledWrapper>
                                            }
                                        >
                                            <GroupActions quote={quote} listGroups={listGroups} currentGroup={key} />
                                            {groups[key] && groups[key].length > 0 && <GroupItemsHeader />}
                                            {(!groups[key] || !groups[key].length) && (
                                                <StyledWrapper {...accodionStyle.emptyGroup} className="with-borders">
                                                    <Typography
                                                        lineHeight={fontConfig.lineHeight.small}
                                                        css={css`
                                                            @media (max-width: 1025px) {
                                                                display: none;
                                                            }
                                                        `}
                                                    >
                                                        Use the&nbsp;&nbsp;
                                                        <Icon src="Drag" size={15} color="common.borders" />
                                                        &nbsp;&nbsp;icon to drag and drop items from other groups.
                                                    </Typography>
                                                    <Typography
                                                        lineHeight={fontConfig.lineHeight.small}
                                                        css={css`
                                                            display: none;
                                                            @media (max-width: 1025px) {
                                                                display: block;
                                                            }
                                                        `}
                                                    >
                                                        {translate(
                                                            "Select items from other groups and move/copy them here",
                                                        )}
                                                    </Typography>
                                                </StyledWrapper>
                                            )}
                                            <StyledWrapper
                                                data-groupkey={`${key}`}
                                                className={`sortable-list ${
                                                    !groups[key] || !groups[key].length ? "empty" : ""
                                                }`}
                                            >
                                                <ReactSortable
                                                    list={groups[key] || []}
                                                    setList={newState =>
                                                        mutateNewGroup(newState as GroupLineModelWithId[], key)
                                                    }
                                                    group="groupName"
                                                    handle=".drag-handle"
                                                    animation={200}
                                                    onAdd={() => {
                                                        newGroupName.current = key;
                                                    }}
                                                    onUpdate={() => {
                                                        oldGroupName.current = key;
                                                        newGroupName.current = key;
                                                    }}
                                                    onStart={(
                                                        evt: Sortable.SortableEvent,
                                                        sortable: Sortable | null,
                                                        store: Store,
                                                    ) => onStart(evt, sortable, store, key)}
                                                    onEnd={syncDataOnEnd}
                                                    ghostClass="sortable-ghost"
                                                    chosenClass="sortable-ghost"
                                                    scrollSensitivity={150}
                                                    emptyInsertThreshold={20}
                                                    scroll
                                                    {...({ forceAutoScrollFallback: true } as any)}
                                                >
                                                    {quote &&
                                                        groups[key] &&
                                                        groups[key].length > 0 &&
                                                        groups[key].map((line: GroupLineModel) => {
                                                            if (!line) {
                                                                return <></>;
                                                            }
                                                            return (
                                                                <StyledWrapper
                                                                    css={css`
                                                                        border-bottom: 1px solid
                                                                            ${getColor("common.ui2")};
                                                                        &&:last-child {
                                                                            border-bottom: 0;
                                                                        }
                                                                        @media print {
                                                                            border-bottom-width: 2px;
                                                                        }
                                                                    `}
                                                                    key={line.lineId}
                                                                    className={
                                                                        line.sortOrder === groups[key]?.length
                                                                            ? "last-item"
                                                                            : ""
                                                                    }
                                                                >
                                                                    <QuoteLine
                                                                        quoteLine={line}
                                                                        groupName={key}
                                                                        quote={quote}
                                                                        addRelatedProduct={addRelatedProductHandler}
                                                                    />
                                                                </StyledWrapper>
                                                            );
                                                        })}
                                                </ReactSortable>
                                            </StyledWrapper>
                                        </ManagedAccordionSection>
                                    </Accordion>
                                </StyledWrapper>
                            </React.Fragment>
                        );
                    }
                    return null;
                })}
                <SaveQtySticky
                    {...styles.saveQtyButton}
                    onClick={updateProductsQty}
                    disabled={productLinesQtyToUpdate.length === 0 || quoteState.isLoading}
                    {...styles.saveQtyButton}
                />
            </StyledWrapper>
            <QuoteTotal quote={quote} />
            <iframe
                id="qrCodeCustomQuote"
                style={{ position: "absolute", height: "0", width: "0", opacity: 0 }}
                name="qrCodeCustomQuote"
            />
            <Modal
                isOpen={changeGroupNameModalIsOpen}
                handleClose={handleChangeGroupModalClose}
                headline={translate("Change Group Name")}
            >
                <TextField
                    type="text"
                    value={newCreatedGroupName}
                    onChange={handleNewGroupNameChange}
                    maxLength={50}
                    label={translate("New Group Name")}
                    error={groupNameError}
                />
                {groupsNamingRegex && (
                    <StyledWrapper {...styles.notifyStyle}>
                        <Notify messageType="info" body={siteMessage("Custom_GroupName_Limitations_Text")} />
                    </StyledWrapper>
                )}
                <StyledWrapper className="modal-actions">
                    <Button onClick={updateGroupName}>{translate("Save")}</Button>
                    <Button variant="secondary" onClick={handleChangeGroupModalClose}>
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <Modal
                size={1100}
                className="add-top-padding"
                headline={translate("Add a Related Product")}
                isOpen={addRelatedProduct}
                handleClose={closeRelatedProductModalHandler}
            >
                <LoadingOverlay loading={isLoadingRelated}>
                    <StyledWrapper {...styles.searchAndProductSection}>
                        <Typography {...styles.addRelatedProductCopy}>
                            {siteMessage("Custom_AddRelatedProductCopy")}
                        </Typography>
                        <RelatedProductSelector
                            selectButtonTitle={translate("Choose")}
                            skipInventoryValidation={true}
                            onSelectProduct={onSelectRelatedProduct}
                            productIsUnavailableMessage={siteMessage("Product_NotFound")}
                        />
                    </StyledWrapper>
                    {relatedError && (
                        <StyledWrapper
                            css={css`
                                margin-bottom: 10px;
                            `}
                        >
                            <Notify messageType="danger" body={relatedError} />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.relatedProductWrapper}>
                        {relatedProducts.length > 0 && relatedProductInfos.length > 0 && <LinesHeader />}
                        {relatedProducts.length > 0 &&
                            relatedProductInfos.length > 0 &&
                            relatedProducts.map((product, index) => (
                                <RelatedProductItem
                                    key={`relatedItem_${product.id}`}
                                    product={product}
                                    productInfo={relatedProductInfos[index]}
                                />
                            ))}
                    </StyledWrapper>

                    <StyledWrapper className="modal-actions">
                        <Button
                            variant="primary"
                            onClick={addRelatedProductToQuote}
                            disabled={relatedProducts.length === 0}
                        >
                            {translate("Add Related Product")}
                        </Button>
                        <Button variant="secondary" onClick={closeRelatedProductModalHandler}>
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </LoadingOverlay>
            </Modal>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(MyCustomQuoteDetailsProductList)),
    definition: {
        displayName: "Product List",
        group: "Custom Quote Details" as any,
        allowedContexts: ["MyCustomQuoteDetailsPage" as any],
    },
};

export default widgetModule;
